import { useEffect } from 'react';

/**
 * Custom hook to dynamically load external scripts
 * @param src - The script source URL
 * @param id - Unique identifier for the script element
 * @param options - Additional script options
 */
const useScript = (
  src: string, 
  id: string, 
  options: {
    async?: boolean;
    defer?: boolean;
    onLoad?: () => void;
    onError?: (error: Event) => void;
  } = {}
) => {
  const { async = true, defer = true, onLoad, onError } = options;

  useEffect(() => {
    // Check if script already exists
    if (document.getElementById(id)) {
      onLoad?.();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.id = id;
    script.async = async;
    script.defer = defer;

    // Add event listeners
    if (onLoad) {
      script.onload = onLoad;
    }
    
    if (onError) {
      script.onerror = onError;
    }

    document.body.appendChild(script);

    // Cleanup function to remove script when component unmounts
    return () => {
      const existingScript = document.getElementById(id);
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, [src, id, async, defer, onLoad, onError]);
};

export default useScript;
